import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { HubCurrentOrders } from './section/hub-current-orders';
import { HubFeed } from './hub-feed';
import { HubHiddenAlerts } from './section/hub-hidden-alerts';
import { HubInsightsSectionSkeleton } from './hub-insights-section-skeleton';
import { HubLowestPerformingCustomers } from './section/hub-lowest-performing-customers';
import { HubMonthlyCommission } from './section/hub-monthly-commission';
import { HubProfitRollup } from './section/hub-profit-rollup';
import { LAYOUT_MAX_WIDTH, TYPOGRAPHY } from '@/styles';
import { OperatorEmployeeContact } from '@/types';
import { PhonePickerBottomSheet } from './phone-picker-bottom-sheet';
import { Platform, ScrollView, Text, View } from 'react-native';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useHubCardOrders } from '@/hooks/insights-hub/use-current-orders';
import { useInsightsHubAlerts } from '@/hooks/insights-hub/use-insights-hub-data';
import { useLayoutContext } from '@/contexts/layout-context';
import { useLowestPerformingCustomersData } from '@/hooks/insights-hub/use-lowest-performing-customers-data';
import { useWeeklyProfitRollupData } from '@/hooks/insights-hub/use-profit-rollup-data';

interface HubGridProps {
  scrollRef?: React.RefObject<ScrollView | null>;
}

export const HubGrid: React.FC<HubGridProps> = ({ scrollRef }) => {
  const { isSmallLayout, isSmallTabletLayout, isLargeLayout } =
    useLayoutContext();
  const [styles] = useStyles();
  const isCurrentOrdersEnabled =
    useDynamicConfigValue('insights_hub_config')?.show_current_orders ?? false;

  // pre-fetch data in order to coodinate loading states
  //   duplicate calls are fine because we are caching the data
  const { loading: activeAlertsLoading } = useInsightsHubAlerts();
  const { loading: profitRollupLoading } = useWeeklyProfitRollupData();
  const { loading: lowestPerformingCustomersLoading } =
    useLowestPerformingCustomersData();
  const { loading: currentOrdersLoading } = useHubCardOrders({
    skip: !isCurrentOrdersEnabled,
  });

  const insightsLoading =
    currentOrdersLoading ||
    profitRollupLoading ||
    lowestPerformingCustomersLoading;

  const mainColumnFlex = isSmallTabletLayout ? 7 : 8;
  const sideColumnFlex = isSmallTabletLayout ? 5 : 4;

  const [operatorContacts, setOperatorContacts] = useState<
    OperatorEmployeeContact[] | null
  >(null);

  return (
    <>
      <ScrollView
        contentContainerStyle={[
          styles.container,
          !isLargeLayout ? styles.mobileHeaderSpacing : null,
          !isLargeLayout && Platform.OS === 'web'
            ? styles.webMobileTopHeader
            : null,
        ]}
        ref={scrollRef}
        scrollEnabled={!activeAlertsLoading}
        showsVerticalScrollIndicator={false}
        style={styles.base}
      >
        {/* Mobile layout */}
        {isSmallLayout ? (
          <Text style={[TYPOGRAPHY.h3, styles.header]}>
            {i18n.t('insights_hub')}
          </Text>
        ) : null}
        {/* Mobile layout */}
        {isSmallLayout ? (
          <View style={styles.cardSection}>
            <HubFeed />
            {/* Hide hidden alerts and insights while active alerts are loading */}
            {!activeAlertsLoading ? (
              <>
                <HubHiddenAlerts />
                <Text style={[TYPOGRAPHY.h3, styles.header2]}>
                  {i18n.t('insights')}
                </Text>
                {/* Show insights section skeleton while any insights section is loading */}
                {!insightsLoading ? (
                  <>
                    {isCurrentOrdersEnabled ? (
                      <HubCurrentOrders
                        openPhonePickerBottomSheet={(operatorEmployees) => {
                          setOperatorContacts(operatorEmployees);
                        }}
                      />
                    ) : null}
                    <HubMonthlyCommission />
                    <HubProfitRollup />
                    <HubLowestPerformingCustomers />
                  </>
                ) : (
                  <HubInsightsSectionSkeleton />
                )}
              </>
            ) : null}
          </View>
        ) : (
          <View style={[styles.cardSection, styles.desktopContainer]}>
            <View style={[styles.cardSection, { flex: mainColumnFlex }]}>
              <HubFeed />
              <HubHiddenAlerts />
            </View>
            <View style={[styles.cardSection, { flex: sideColumnFlex }]}>
              <HubMonthlyCommission />
              <HubProfitRollup />
              <HubLowestPerformingCustomers />
            </View>
          </View>
        )}
      </ScrollView>
      {operatorContacts && operatorContacts.length > 0 ? (
        <PhonePickerBottomSheet
          contacts={operatorContacts}
          onDismiss={() => setOperatorContacts(null)}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    marginHorizontal: 16,
    paddingVertical: 24,
  },
  cardSection: {
    gap: 8,
  },
  container: {
    justifyContent: 'center',
    width: '100%',
  },
  desktopContainer: {
    flexDirection: 'row',
    marginHorizontal: 'auto',
    maxWidth: LAYOUT_MAX_WIDTH,
    width: '100%',
  },
  header: {
    color: theme.colors.primary,
    fontSize: 25,
    fontWeight: 600,
    lineHeight: 33.8,
    marginVertical: 24,
  },
  header2: {
    color: theme.colors.primary,
    fontSize: 21,
    lineHeight: 31.5,
    marginBottom: 12,
    marginTop: 24,
  },
  mobileHeaderSpacing: {
    paddingBottom: 64,
    paddingTop: 80,
  },
  webMobileTopHeader: {
    paddingTop: 24,
  },
}));
